/**
 * DocUploadField.js
 * Shared document upload component used by BusinessRegisterPage and NewBusinessRegisterPage.
 *
 * Accepted formats : PDF, JPEG, PNG  (SVG removed)
 * On JPEG / PNG    : compress with browser-image-compression, then embed into a PDF via pdf-lib
 * On PDF           : compress (re-save) via pdf-lib
 * Result           : always uploads a PDF to the server
 *
 * Dependencies (add to package.json):
 *   "browser-image-compression": "^2.0.2"
 *   "pdf-lib": "^1.17.1"
 */

import React, { useRef, useState } from "react";
import { FiUpload, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
import { PDFDocument } from "pdf-lib";
import API from "../../utils/api";

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Compress a JPEG / PNG File, then embed it as a single-page PDF.
 * Returns a Blob with mime-type application/pdf.
 */
async function imageToPdfBlob(file) {
  // 1. Compress the image first (target ≤ 1 MB, max dimension 1920 px)
  const compressed = await imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });

  // 2. Read compressed bytes
  const imgBytes = await compressed.arrayBuffer();

  // 3. Create a new PDF and embed the image
  const pdfDoc = await PDFDocument.create();
  let pdfImage;
  if (file.type === "image/png") {
    pdfImage = await pdfDoc.embedPng(imgBytes);
  } else {
    // jpeg / jpg
    pdfImage = await pdfDoc.embedJpg(imgBytes);
  }

  // 4. Add a page sized exactly to the image
  const page = pdfDoc.addPage([pdfImage.width, pdfImage.height]);
  page.drawImage(pdfImage, {
    x: 0,
    y: 0,
    width: pdfImage.width,
    height: pdfImage.height,
  });

  // 5. Save as compressed PDF bytes
  const pdfBytes = await pdfDoc.save({ useObjectStreams: true });
  return new Blob([pdfBytes], { type: "application/pdf" });
}

/**
 * Re-save a PDF via pdf-lib to apply object-stream compression.
 * Returns a Blob with mime-type application/pdf.
 */
async function compressPdfBlob(file) {
  const fileBytes = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(fileBytes, {
    ignoreEncryption: true,
  });
  const pdfBytes = await pdfDoc.save({ useObjectStreams: true });
  return new Blob([pdfBytes], { type: "application/pdf" });
}

// ─── component ───────────────────────────────────────────────────────────────

const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB raw input limit (will be compressed)

/**
 * Props:
 *   label      – string shown above the drop zone
 *   optional   – if true, appends "(optional)"
 *   fieldName  – key passed back to onChange
 *   value      – { url, name } | null
 *   onChange   – (fieldName, { url, name } | null) => void
 */
const DocUploadField = ({ label, optional, fieldName, value, onChange }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only PDF, PNG, or JPEG files are allowed.");
      return;
    }

    // Validate raw size
    if (file.size > MAX_BYTES) {
      toast.error("File must be under 10 MB.");
      return;
    }

    setProcessing(true);
    const processingToast = toast.loading(
      file.type === "application/pdf"
        ? "Compressing PDF…"
        : "Compressing and converting to PDF…"
    );

    try {
      let blob;
      let uploadName;

      if (file.type === "application/pdf") {
        blob = await compressPdfBlob(file);
        uploadName = file.name;
      } else {
        // JPEG or PNG → convert to PDF
        blob = await imageToPdfBlob(file);
        uploadName = file.name.replace(/\.(jpe?g|png)$/i, ".pdf");
      }

      // Build FormData and upload
      const formData = new FormData();
      formData.append("image", blob, uploadName);

      const res = await API.post("/upload/public", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.dismiss(processingToast);

      if (res.data.success) {
        onChange(fieldName, { url: res.data.url, name: uploadName });
        toast.success("Document uploaded successfully.");
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (err) {
      toast.dismiss(processingToast);
      console.error("DocUploadField error:", err);
      toast.error("Processing failed. Please try again.");
    } finally {
      setProcessing(false);
      // Reset input so the same file can be re-selected if removed
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    handleFile({ target: { files: [file] } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  return (
    <div className="file-upload-field">
      <label className="file-upload-label">
        {label}
        {optional && <span className="optional"> (optional)</span>}
      </label>

      {value?.url ? (
        <div className="file-preview">
          <div className="file-preview-doc">
            <span className="file-icon">📄</span>
            <span className="file-name">{value.name || "Document"}</span>
          </div>
          <button
            type="button"
            className="file-remove-btn"
            onClick={() => onChange(fieldName, null)}
            title="Remove file"
          >
            <FiX size={14} />
          </button>
        </div>
      ) : (
        <div
          className={`file-upload-zone ${dragging ? "dragging" : ""} ${processing ? "uploading" : ""}`}
          onClick={() => !processing && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <FiUpload size={20} />
          <span>{processing ? "Processing…" : "Click or drag & drop"}</span>
          <span className="file-hint">PDF, PNG, JPEG • max 10 MB</span>
          <span className="file-hint">Images are auto-converted &amp; compressed to PDF</span>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        style={{ display: "none" }}
        onChange={handleFile}
        disabled={processing}
      />
    </div>
  );
};

export default DocUploadField;